/**
 * ApplicationCatalogueService
 *
 * Service for the CCMS mobile constellation catalogue
 * (`CCMS.Api.ApplicationCatalogue`). Backs the Hub launcher, the web "Apps"
 * page, and the catalogue admin UI.
 *
 * Routes (gateway prefix `application-catalogue`):
 *  - GET    /api/catalogue                 — entitlement-filtered, enabled-only list (caller)
 *  - GET    /api/catalogue/{appId}          — single full manifest (caller)
 *  - GET    /api/catalogue/all              — every manifest, unfiltered (admin)
 *  - POST   /api/catalogue                  — create a manifest (admin)
 *  - PUT    /api/catalogue/{appId}          — update a manifest (admin)
 *  - DELETE /api/catalogue/{appId}          — delete a manifest (admin)
 *  - PATCH  /api/catalogue/{appId}/toggle   — flip / set enabled state (admin)
 */
import { BaseService } from '../base/BaseService';
/**
 * Service for application catalogue read + admin operations.
 */
export class ApplicationCatalogueService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? ApplicationCatalogueService.DEFAULT_SERVICE_PATH,
            basePath: ApplicationCatalogueService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== CALLER (entitlement-filtered) ====================
    /**
     * Get the entitlement-filtered, enabled-only catalogue for the signed-in user.
     * GET /api/catalogue
     * @returns Array of manifests the caller is entitled to see.
     */
    async getCatalogue() {
        const response = await this.getCatalogueRaw();
        return response.data ?? [];
    }
    /**
     * Get the entitlement-filtered catalogue (raw response).
     */
    getCatalogueRaw() {
        const url = this.buildUrl();
        return this.http.get(url);
    }
    /**
     * Get a single full manifest for the signed-in user (entitlement-checked).
     * GET /api/catalogue/{appId}
     * @param appId - Stable app id (e.g. `ccms.task`).
     * @throws Error if the app is not found or the caller is not entitled.
     */
    async getApp(appId) {
        const response = await this.getAppRaw(appId);
        if (!response.data) {
            throw new Error(`App '${appId}' not found or not available`);
        }
        return response.data;
    }
    /**
     * Get a single manifest (raw response).
     * @param appId - Stable app id.
     */
    getAppRaw(appId) {
        const url = this.buildUrl(encodeURIComponent(appId));
        return this.http.get(url);
    }
    // ==================== ADMIN ====================
    /**
     * Get every manifest in the catalogue, unfiltered (admin only).
     * GET /api/catalogue/all
     * @returns Array of all manifests.
     */
    async getAllApps() {
        const response = await this.getAllAppsRaw();
        return response.data ?? [];
    }
    /**
     * Get every manifest (raw response).
     */
    getAllAppsRaw() {
        const url = this.buildUrl('all');
        return this.http.get(url);
    }
    /**
     * Create a new app manifest (admin only).
     * POST /api/catalogue
     * @param manifest - The manifest to create.
     * @throws Error if creation / validation fails.
     */
    async createApp(manifest) {
        const response = await this.createAppRaw(manifest);
        if (!response.data) {
            throw new Error(response.message ?? 'Failed to create app manifest');
        }
        return response.data;
    }
    /**
     * Create a new app manifest (raw response).
     * @param manifest - The manifest to create.
     */
    createAppRaw(manifest) {
        const url = this.buildUrl();
        return this.http.post(url, manifest);
    }
    /**
     * Update an existing app manifest (admin only).
     * PUT /api/catalogue/{appId}
     * @param appId - Stable app id of the manifest to update.
     * @param manifest - The full updated manifest.
     * @throws Error if update / validation fails.
     */
    async updateApp(appId, manifest) {
        const response = await this.updateAppRaw(appId, manifest);
        if (!response.data) {
            throw new Error(response.message ?? `Failed to update app '${appId}'`);
        }
        return response.data;
    }
    /**
     * Update an existing app manifest (raw response).
     * @param appId - Stable app id.
     * @param manifest - The full updated manifest.
     */
    updateAppRaw(appId, manifest) {
        const url = this.buildUrl(encodeURIComponent(appId));
        return this.http.put(url, manifest);
    }
    /**
     * Delete an app manifest (admin only).
     * DELETE /api/catalogue/{appId}
     * @param appId - Stable app id.
     * @returns True if the manifest was deleted.
     */
    async deleteApp(appId) {
        const response = await this.deleteAppRaw(appId);
        return response.success;
    }
    /**
     * Delete an app manifest (raw response).
     * @param appId - Stable app id.
     */
    deleteAppRaw(appId) {
        const url = this.buildUrl(encodeURIComponent(appId));
        return this.http.delete(url);
    }
    /**
     * Toggle / set the enabled state of an app manifest (admin only).
     * PATCH /api/catalogue/{appId}/toggle
     *
     * When `enabled` is omitted the server flips the current state. When provided
     * it sets the explicit state. Disabling hides the tile from launchers
     * (Appendix E).
     *
     * @param appId - Stable app id.
     * @param enabled - Optional explicit target state. Omit to flip.
     * @throws Error if the app is not found.
     */
    async toggleApp(appId, enabled) {
        const response = await this.toggleAppRaw(appId, enabled);
        if (!response.data) {
            throw new Error(response.message ?? `Failed to toggle app '${appId}'`);
        }
        return response.data;
    }
    /**
     * Toggle / set the enabled state (raw response).
     * @param appId - Stable app id.
     * @param enabled - Optional explicit target state.
     */
    toggleAppRaw(appId, enabled) {
        const url = this.buildUrlWithParams(`${encodeURIComponent(appId)}/toggle`, enabled === undefined ? undefined : { enabled });
        return this.http.patch(url);
    }
}
ApplicationCatalogueService.DEFAULT_SERVICE_PATH = 'application-catalogue';
ApplicationCatalogueService.BASE_PATH = 'api/catalogue';
//# sourceMappingURL=ApplicationCatalogueService.js.map